import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { db } from '../firebase';
import { Bell, CheckCircle } from 'lucide-react';
import { collection, getDocs, query, doc, getDoc, addDoc, updateDoc, serverTimestamp } from 'firebase/firestore';

function PublicMenu() {
  const { storeId } = useParams();
  const [searchParams] = useSearchParams();
  const tableParam = searchParams.get('table');

  const [menus, setMenus] = useState([]);
  const [categories, setCategories] = useState([]);
  const [storeName, setStoreName] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [plan, setPlan] = useState('free');
  const [lang, setLang] = useState('ja');
  const [activeCategory, setActiveCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [calling, setCalling] = useState(false);
  const [callDone, setCallDone] = useState(false);
  const [storeUid, setStoreUid] = useState('');
  const [cart, setCart] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [orderSent, setOrderSent] = useState(false);

  useEffect(() => {
    const browserLang = navigator.language || 'ja';
    if (browserLang.startsWith('zh-TW') || browserLang.startsWith('zh-HK')) setLang('zh-TW');
    else if (browserLang.startsWith('zh')) setLang('zh-CN');
    else if (browserLang.startsWith('ko')) setLang('ko');
    else if (browserLang.startsWith('en')) setLang('en');
    else setLang('ja');
  }, []);

  useEffect(() => {
    async function fetchData() {
      try {
        const idSnap = await getDoc(doc(db, 'storeIds', storeId));
        let uid = storeId;
        if (idSnap.exists()) uid = idSnap.data().uid;
        setStoreUid(uid);

        const storeDoc = await getDoc(doc(db, 'stores', uid));
        if (!storeDoc.exists()) { setNotFound(true); setLoading(false); return; }
        const data = storeDoc.data();
        setStoreName(data.storeName);
        setLogoUrl(data.logoUrl || '');
        setPlan(data.plan || 'free');

        const catsSnap = await getDocs(collection(db, 'stores', uid, 'categories'));
        const cats = catsSnap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (a.order || 0) - (b.order || 0));
        setCategories(cats);

        const q = query(collection(db, 'stores', uid, 'menus'));
        const snapshot = await getDocs(q);
        const menuData = snapshot.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
        setMenus(menuData);
        setLoading(false);
      } catch { setNotFound(true); setLoading(false); }
    }
    fetchData();
  }, [storeId]);

  const isStandard = plan === 'standard' || plan === 'premium' || plan === 'pro';

  const languages = isStandard ? [
    { code: 'ja', label: '日本語' },
    { code: 'en', label: 'EN' },
    { code: 'zh-CN', label: '简体' },
    { code: 'zh-TW', label: '繁體' },
    { code: 'ko', label: '한국어' },
  ] : [
    { code: 'ja', label: '日本語' },
    { code: 'en', label: 'EN' },
  ];

  const callLabels = {
    ja: '店員を呼ぶ',
    en: 'Call Staff',
    'zh-CN': '呼叫服务员',
    'zh-TW': '呼叫服務員',
    ko: '직원 호출',
  };

  const callDoneLabels = {
    ja: '店員が向かいます',
    en: 'Staff is coming',
    'zh-CN': '服务员正在赶来',
    'zh-TW': '服務員正在趕來',
    ko: '직원이 오고 있습니다',
  };

  const orderLabels = {
    ja: '注文する',
    en: 'Order',
    'zh-CN': '下单',
    'zh-TW': '下單',
    ko: '주문하기',
  };

  const orderSendingLabels = {
    ja: '送信中...',
    en: 'Sending...',
    'zh-CN': '发送中...',
    'zh-TW': '發送中...',
    ko: '전송 중...',
  };

  const orderSentLabels = {
    ja: 'ご注文を承りました',
    en: 'Order received',
    'zh-CN': '已收到您的订单',
    'zh-TW': '已收到您的訂單',
    ko: '주문이 접수되었습니다',
  };

  const orderWaitLabels = {
    ja: 'スタッフが確認いたします',
    en: 'Staff will confirm shortly',
    'zh-CN': '工作人员将进行确认',
    'zh-TW': '工作人員將進行確認',
    ko: '직원이 확인해 드리겠습니다',
  };

  const orderErrorLabels = {
    ja: 'エラーが発生しました。もう一度お試しください。',
    en: 'Something went wrong. Please try again.',
    'zh-CN': '发生错误，请重试。',
    'zh-TW': '發生錯誤，請重試。',
    ko: '오류가 발생했습니다. 다시 시도해 주세요.',
  };

  async function handleCallStaff() {
    if (calling || callDone || !storeUid || !tableParam) return;
    setCalling(true);
    try {
      await addDoc(collection(db, 'stores', storeUid, 'calls'), {
        tableName: tableParam,
        status: 'pending',
        createdAt: serverTimestamp(),
      });
      setCallDone(true);
      setTimeout(() => setCallDone(false), 30000);
    } catch (err) {
      alert('エラーが発生しました。もう一度お試しください。');
    }
    setCalling(false);
  }

  function updateCartQty(menuId, delta) {
    setCart(prev => {
      const next = Math.max(0, (prev[menuId] || 0) + delta);
      if (next === 0) {
        const { [menuId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [menuId]: next };
    });
  }

  const cartEntries = Object.entries(cart).filter(([, qty]) => qty > 0);
  const cartCount = cartEntries.reduce((sum, [, qty]) => sum + qty, 0);
  const cartTotal = cartEntries.reduce((sum, [menuId, qty]) => {
    const menu = menus.find(m => m.id === menuId);
    return sum + (menu ? menu.price * qty : 0);
  }, 0);

  async function handleSubmitOrder() {
    if (submitting || cartEntries.length === 0 || !storeUid || !tableParam) return;
    setSubmitting(true);
    try {
      const items = cartEntries.map(([menuId, quantity]) => {
        const menu = menus.find(m => m.id === menuId);
        return { menuId, nameJa: menu.nameJa, nameEn: menu.nameEn || '', price: menu.price, quantity, status: 'cooking' };
      });
      const totalPrice = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
      await addDoc(collection(db, 'stores', storeUid, 'orders'), {
        tableId: '',
        tableName: tableParam,
        items,
        totalPrice,
        status: 'open',
        createdAt: serverTimestamp(),
      });
      await Promise.all(items.map(item =>
        updateDoc(doc(db, 'stores', storeUid, 'menus', item.menuId), {
          soldCount: (menus.find(m => m.id === item.menuId)?.soldCount || 0) + item.quantity
        })
      ));
      setCart({});
      setOrderSent(true);
      setTimeout(() => setOrderSent(false), 5000);
    } catch (err) {
      alert(orderErrorLabels[lang] || orderErrorLabels.ja);
    }
    setSubmitting(false);
  }

  function getName(menu) {
    if (lang === 'en') return menu.nameEn || menu.nameJa;
    if (lang === 'zh-CN') return menu.nameZhCN || menu.nameJa;
    if (lang === 'zh-TW') return menu.nameZhTW || menu.nameJa;
    if (lang === 'ko') return menu.nameKo || menu.nameJa;
    return menu.nameJa;
  }

  function getDesc(menu) {
    if (lang === 'en') return menu.descEn || menu.descJa || '';
    if (lang === 'zh-CN') return menu.descZhCN || menu.descJa || '';
    if (lang === 'zh-TW') return menu.descZhTW || menu.descJa || '';
    if (lang === 'ko') return menu.descKo || menu.descJa || '';
    return menu.descJa || '';
  }

  function getCategoryName(cat) {
    if (lang === 'en') return cat.nameEn || cat.nameJa;
    if (lang === 'zh-CN') return cat.nameZhCN || cat.nameJa;
    if (lang === 'zh-TW') return cat.nameZhTW || cat.nameJa;
    if (lang === 'ko') return cat.nameKo || cat.nameJa;
    return cat.nameJa;
  }

  const filteredMenus = activeCategory === 'all'
    ? menus
    : activeCategory === 'uncategorized'
    ? menus.filter(m => !m.categoryId)
    : menus.filter(m => m.categoryId === activeCategory);

  const available = filteredMenus.filter(m => m.status !== 'soldout');
  const soldout = filteredMenus.filter(m => m.status === 'soldout');

  const groupedMenus = () => {
    if (activeCategory !== 'all') return [{ category: null, menus: available }];
    const groups = [];
    categories.forEach(cat => {
      const items = available.filter(m => m.categoryId === cat.id);
      if (items.length > 0) groups.push({ category: cat, menus: items });
    });
    const uncategorized = available.filter(m => !m.categoryId);
    if (uncategorized.length > 0) groups.push({ category: null, menus: uncategorized });
    return groups;
  };

  if (loading) return (
    <div style={styles.loadingWrap}><p style={styles.loadingText}>読み込み中...</p></div>
  );
  if (notFound) return (
    <div style={styles.loadingWrap}><p style={styles.loadingText}>お店が見つかりませんでした</p></div>
  );

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerTop}>
          {logoUrl ? (
            <img src={logoUrl} alt={storeName} style={styles.logoImg} />
          ) : (
            <div style={styles.logoPlaceholder}>
              <span style={styles.logoText}>{storeName ? storeName.charAt(0) : '店'}</span>
            </div>
          )}
          <div>
            <h1 style={styles.storeName}>{storeName}</h1>
            {tableParam && (
              <p style={styles.tableTag}>📍 {tableParam}</p>
            )}
            <p style={styles.powered}>Powered by MenuSore</p>
          </div>
        </div>

        <div style={styles.categoryTabs}>
          <button onClick={() => setActiveCategory('all')}
            style={activeCategory === 'all' ? styles.tabActive : styles.tab}>
            {lang === 'ja' ? 'すべて' : lang === 'en' ? 'All' : lang === 'ko' ? '전체' : '全部'}
          </button>
          {categories.map(cat => (
            <button key={cat.id} onClick={() => setActiveCategory(cat.id)}
              style={activeCategory === cat.id ? styles.tabActive : styles.tab}>
              {getCategoryName(cat)}
            </button>
          ))}
          {menus.some(m => !m.categoryId) && categories.length > 0 && (
            <button onClick={() => setActiveCategory('uncategorized')}
              style={activeCategory === 'uncategorized' ? styles.tabActive : styles.tab}>
              {lang === 'ja' ? 'その他' : 'Other'}
            </button>
          )}
        </div>
      </div>

      <div style={styles.langBar}>
        {languages.map(l => (
          <button key={l.code} onClick={() => setLang(l.code)}
            style={lang === l.code ? styles.langActive : styles.langBtn}>
            {l.label}
          </button>
        ))}
      </div>

      <div style={styles.menuList}>
        {groupedMenus().map((group, i) => (
          <div key={i}>
            {group.category && (
              <div style={styles.sectionHeader}>
                <span style={styles.sectionTitle}>{getCategoryName(group.category)}</span>
              </div>
            )}
            {activeCategory === 'all' && !group.category && categories.length > 0 && (
              <div style={styles.sectionHeader}>
                <span style={styles.sectionTitle}>{lang === 'ja' ? 'その他' : 'Other'}</span>
              </div>
            )}
            {group.menus.map(menu => (
              <div key={menu.id} style={styles.menuItem}>
                <div style={styles.menuBody}>
                  <p style={styles.menuName}><span style={{fontSize:12, fontWeight:700, color:"#2d6a4f", marginRight:4}}>No.{menu.menuNumber || "—"}</span>{getName(menu)}</p>
                  {getDesc(menu) ? <p style={styles.desc}>{getDesc(menu)}</p> : null}
                  <p style={styles.price}>¥{Number(menu.price).toLocaleString()}</p>
                  {isStandard && tableParam && (
                    <div style={styles.qtyRow}>
                      <button onClick={() => updateCartQty(menu.id, -1)} disabled={!cart[menu.id]}
                        style={{ ...styles.qtyBtn, opacity: cart[menu.id] ? 1 : 0.4 }}>−</button>
                      <span style={styles.qtyCount}>{cart[menu.id] || 0}</span>
                      <button onClick={() => updateCartQty(menu.id, 1)} style={styles.qtyBtn}>＋</button>
                    </div>
                  )}
                </div>
                {menu.imageUrl ? (
                  <img src={menu.imageUrl} alt={getName(menu)} style={styles.menuThumb} />
                ) : null}
              </div>
            ))}
          </div>
        ))}

        {soldout.length > 0 && (
          <>
            <div style={styles.divider}>
              {lang === 'ja' ? '売り切れ' : lang === 'en' ? 'Sold Out' : lang === 'zh-CN' ? '售罄' : lang === 'zh-TW' ? '售罄' : '품절'}
            </div>
            {soldout.map(menu => (
              <div key={menu.id} style={{...styles.menuItem, opacity: 0.4}}>
                <div style={styles.menuBody}>
                  <p style={styles.menuName}><span style={{fontSize:12, fontWeight:700, color:"#2d6a4f", marginRight:4}}>No.{menu.menuNumber || "—"}</span>{getName(menu)}</p>
                  <p style={styles.soldoutLabel}>SOLD OUT</p>
                </div>
                {menu.imageUrl ? (
                  <img src={menu.imageUrl} alt={getName(menu)} style={styles.menuThumb} />
                ) : null}
              </div>
            ))}
          </>
        )}

        {available.length === 0 && soldout.length === 0 && (
          <p style={styles.empty}>メニューがありません</p>
        )}
      </div>

      {isStandard && tableParam && (
        <div style={styles.callArea}>
          {orderSent ? (
            <div style={styles.orderSentBox}>
              <p style={styles.orderSentTitle}>
                <CheckCircle size={18} style={{display:'inline',verticalAlign:'middle',marginRight:6}} />
                {orderSentLabels[lang] || orderSentLabels.ja}
              </p>
              <p style={styles.callNote}>{orderWaitLabels[lang] || orderWaitLabels.ja}</p>
            </div>
          ) : cartCount > 0 ? (
            <>
              <button onClick={handleSubmitOrder} disabled={submitting} style={styles.cartBtn}>
                <span>{submitting ? (orderSendingLabels[lang] || orderSendingLabels.ja) : `${orderLabels[lang] || orderLabels.ja} (${cartCount})`}</span>
                <span>¥{cartTotal.toLocaleString()}</span>
              </button>
              <button onClick={handleCallStaff} disabled={calling || callDone} style={styles.callLinkSmall}>
                {callDone ? (callDoneLabels[lang] || callDoneLabels.ja) : (callLabels[lang] || callLabels.ja)}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleCallStaff}
                disabled={calling || callDone}
                style={callDone ? styles.callBtnDone : styles.callBtn}>
                {calling ? '...' : callDone
                  ? <><CheckCircle size={18} style={{display:'inline',verticalAlign:'middle',marginRight:6}} />{callDoneLabels[lang] || callDoneLabels.ja}</>
                  : <><Bell size={18} style={{display:'inline',verticalAlign:'middle',marginRight:6}} />{callLabels[lang] || callLabels.ja}</>}
              </button>
              {callDone && (
                <p style={styles.callNote}>
                  {lang === 'ja' ? 'しばらくお待ちください' :
                   lang === 'en' ? 'Please wait a moment' :
                   lang === 'zh-CN' ? '请稍候' :
                   lang === 'zh-TW' ? '請稍候' : '잠시만 기다려 주세요'}
                </p>
              )}
            </>
          )}
        </div>
      )}

      <div style={styles.footer}>
        <p style={styles.footerText}>Powered by MenuSore</p>
      </div>
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', background: '#fff',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Hiragino Sans", sans-serif',
    maxWidth: '640px', margin: '0 auto', paddingBottom: '100px' },
  loadingWrap: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  loadingText: { color: '#999', fontSize: '14px' },
  header: { background: '#2d6a4f', color: '#fff', padding: '20px 16px 12px',
    position: 'sticky', top: 0, zIndex: 10 },
  headerTop: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' },
  logoImg: { width: '48px', height: '48px', borderRadius: '50%',
    objectFit: 'cover', border: '2px solid rgba(255,255,255,0.5)', flexShrink: 0 },
  logoPlaceholder: { width: '48px', height: '48px', borderRadius: '50%',
    background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center',
    justifyContent: 'center', flexShrink: 0 },
  logoText: { color: '#fff', fontSize: '20px', fontWeight: '700' },
  storeName: { margin: '0 0 2px', fontSize: '18px', fontWeight: '700' },
  tableTag: { margin: '0 0 2px', fontSize: '11px', opacity: 0.9,
    background: 'rgba(255,255,255,0.2)', display: 'inline-block',
    padding: '1px 8px', borderRadius: '10px' },
  powered: { margin: 0, fontSize: '10px', opacity: 0.6 },
  categoryTabs: { display: 'flex', gap: '6px', overflowX: 'auto',
    paddingBottom: '2px', msOverflowStyle: 'none', scrollbarWidth: 'none' },
  tab: { padding: '5px 14px', background: 'rgba(255,255,255,0.2)', color: '#fff',
    border: 'none', borderRadius: '20px', cursor: 'pointer',
    fontSize: '12px', whiteSpace: 'nowrap', flexShrink: 0 },
  tabActive: { padding: '5px 14px', background: 'rgba(255,255,255,0.95)', color: '#2d6a4f',
    border: 'none', borderRadius: '20px', cursor: 'pointer',
    fontSize: '12px', fontWeight: '700', whiteSpace: 'nowrap', flexShrink: 0 },
  langBar: { display: 'flex', gap: '6px', padding: '8px 16px',
    background: '#f0faf4', borderBottom: '0.5px solid #b7e4c7', overflowX: 'auto' },
  langBtn: { padding: '4px 12px', background: 'transparent', color: '#2d6a4f',
    border: '0.5px solid #2d6a4f', borderRadius: '20px',
    cursor: 'pointer', fontSize: '12px', whiteSpace: 'nowrap', flexShrink: 0 },
  langActive: { padding: '4px 12px', background: '#2d6a4f', color: '#fff',
    border: '0.5px solid #2d6a4f', borderRadius: '20px',
    cursor: 'pointer', fontSize: '12px', fontWeight: '700',
    whiteSpace: 'nowrap', flexShrink: 0 },
  sectionHeader: { background: '#f0faf4', padding: '8px 20px',
    borderBottom: '0.5px solid #d8f3dc', marginTop: '4px' },
  sectionTitle: { fontSize: '13px', fontWeight: '700', color: '#2d6a4f' },
  menuList: { padding: '4px 0 20px' },
  menuItem: { display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 20px', borderBottom: '1px solid #f5f5f5', gap: '16px' },
  menuBody: { flex: 1, minWidth: 0 },
  menuName: { fontSize: '15px', color: '#1a1a2e', fontWeight: '500',
    lineHeight: '1.4', margin: '0 0 4px' },
  desc: { fontSize: '12px', color: '#999', lineHeight: '1.5', margin: '0 0 6px',
    display: '-webkit-box', WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical', overflow: 'hidden' },
  price: { fontSize: '15px', fontWeight: '700', color: '#2d6a4f', margin: 0 },
  menuThumb: { width: '80px', height: '80px', objectFit: 'cover',
    borderRadius: '10px', flexShrink: 0, border: '1px solid #f0f0f0' },
  soldoutLabel: { fontSize: '11px', color: '#c00', fontWeight: '700', margin: 0 },
  divider: { textAlign: 'center', padding: '10px 20px', color: '#bbb',
    fontSize: '12px', background: '#fafafa',
    borderTop: '1px solid #f0f0f0', borderBottom: '1px solid #f0f0f0', margin: '8px 0' },
  empty: { textAlign: 'center', color: '#bbb', padding: '40px 20px', fontSize: '14px' },
  callArea: { position: 'fixed', bottom: 0, left: '50%', transform: 'translateX(-50%)',
    width: '100%', maxWidth: '640px', padding: '12px 20px 24px',
    background: 'linear-gradient(to top, #fff 80%, transparent)',
    textAlign: 'center', zIndex: 20 },
  callBtn: { width: '100%', padding: '16px', background: '#f4a261', color: '#fff',
    border: 'none', borderRadius: '14px', fontSize: '16px', fontWeight: '700',
    cursor: 'pointer', boxShadow: '0 4px 16px rgba(244,162,97,0.4)' },
  callBtnDone: { width: '100%', padding: '16px', background: '#2d6a4f', color: '#fff',
    border: 'none', borderRadius: '14px', fontSize: '16px', fontWeight: '700',
    cursor: 'default', boxShadow: 'none' },
  callNote: { fontSize: '12px', color: '#999', margin: '6px 0 0' },
  qtyRow: { display: 'flex', alignItems: 'center', gap: '10px', marginTop: '8px' },
  qtyBtn: { width: '28px', height: '28px', borderRadius: '8px', border: '1px solid #b7e4c7',
    background: '#f0faf4', color: '#2d6a4f', fontSize: '16px', fontWeight: '700',
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
    lineHeight: 1, padding: 0 },
  qtyCount: { minWidth: '20px', textAlign: 'center', fontSize: '14px',
    fontWeight: '700', color: '#1a1a2e' },
  cartBtn: { width: '100%', padding: '16px 20px', background: '#2d6a4f', color: '#fff',
    border: 'none', borderRadius: '14px', fontSize: '16px', fontWeight: '700',
    cursor: 'pointer', boxShadow: '0 4px 16px rgba(45,106,79,0.4)',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  callLinkSmall: { marginTop: '8px', background: 'none', border: 'none',
    color: '#888', fontSize: '12px', textDecoration: 'underline', cursor: 'pointer' },
  orderSentBox: { background: '#fff', borderRadius: '14px', padding: '14px',
    boxShadow: '0 4px 16px rgba(0,0,0,0.1)' },
  orderSentTitle: { margin: 0, fontSize: '15px', fontWeight: '700', color: '#2d6a4f' },
  footer: { textAlign: 'center', padding: '20px', borderTop: '1px solid #f0f0f0' },
  footerText: { color: '#ccc', fontSize: '12px', margin: 0 },
};

export default PublicMenu;
